import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { EventLogEntry, DwellerMovement, Quest, EraIndex, VentureType } from '../types';
import type { BunkerSummaryWire, FounderStatsWire } from '../types/ws';
import { isInvokable } from '../data/dwellers';
import { QUESTS } from '../data/quests';
import { ERAS } from '../data/eras';
import { type ThemeId, loadTheme, saveTheme, applyTheme, nextTheme } from '../lib/themes';
import { ERA_CLEAR_XP, xpBreakdown, rankForLevel } from '../data/xp';
import { setApiBunkerId } from '../lib/apiClient';

const MAX_EVENTS = 50;

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

export interface ThinkingState {
  startedAt: number;
  inputTokens: number;
}

interface GameStore {
  selectedDwellerId: string | null;
  /** Era label for display, e.g. "Era 0 — Identity" */
  currentEraLabel: string;
  /** Era index 0..5, controls floor unlocks. */
  currentEraIndex: EraIndex;
  /** MGT-413 phase index 0..12, identifies the active quest's phase. Auto-tracks era boundaries. */
  currentMgtPhase: number;
  /** Founder-declared venture type, null until Phase 0 sets it. Affects Designer's Era 3 unlock. */
  ventureType: VentureType | null;
  /** True once the Overseer has completed the Phase 0 onboarding ritual. */
  onboardingComplete: boolean;
  /** Effective billing plan, server-owned; applied from session_state on every connect. */
  plan: 'free' | 'founder' | 'beta';
  /** Set when the server blocks an action behind a paywall (e.g. era gate). Null when no prompt is showing. */
  upgradePrompt: { era: number; reason: string } | null;
  /** Live WS link state. 'connecting' until first open; 'reconnecting' after a drop. Not persisted. */
  wsStatus: 'connecting' | 'connected' | 'reconnecting';
  /** True once the server's session_state push has been applied this session. Not persisted. */
  sessionHydrated: boolean;
  /** Active UI chrome theme. Persisted by src/lib/themes.ts (its own localStorage key). */
  theme: ThemeId;
  /** All bunkers from the server index. Server-owned; not persisted. */
  bunkers: BunkerSummaryWire[];
  /** True once the server's bunkers_list has been received at least once. */
  bunkersLoaded: boolean;
  /** Global founder stats (tokens / active-time / streak). Server-owned; not persisted. */
  founderStats: FounderStatsWire | null;
  /** The bunker id the client is currently viewing (route :id). */
  activeBunkerId: string | null;
  /** Which bunker the persisted quest done-state belongs to. Used to wipe the
   *  board when entering a different venture (quests are per-bunker). */
  questsBunkerId: string | null;
  /** Set by bunker_created so HomePage can navigate into the fresh bunker. */
  justCreatedBunkerId: string | null;
  events: EventLogEntry[];
  dwellerMovements: Record<string, DwellerMovement>;
  chats: Record<string, ChatMessage[]>;
  thinkingAgents: Record<string, ThinkingState>;
  quests: Record<string, Quest>;
  pendingUnlockAnimation: number | null;
  inboxBump: Record<string, number>;
  historyBump: Record<string, number>;
  artifactBump: Record<string, number>;
  /** Bumped each time a conversation is explicitly loaded from history — the
   *  terminal watches this to force a full re-render regardless of msg count. */
  conversationEpoch: Record<string, number>;
  /** How the terminal should label the next epoch-driven replay: 'restored' for a
   *  history load, 'fresh' for a brand-new thread (quest start). */
  conversationMode: Record<string, 'restored' | 'fresh'>;
  /** The saved conversation file the client considers active for each agent.
   *  `string` → server updates it; `null` → next invoke forces a new file;
   *  `undefined` → no info, server uses its per-socket memory. */
  activeConversationFile: Record<string, string | null | undefined>;
  selectDweller: (id: string | null) => void;
  pushEvent: (text: string) => void;
  resetEvents: () => void;
  dispatchDweller: (id: string, targetRoomId: string) => void;
  _setMovement: (id: string, patch: Partial<DwellerMovement>) => void;
  appendChatMessage: (agentId: string, role: 'user' | 'assistant', content: string) => void;
  clearChat: (agentId: string) => void;
  loadConversation: (agentId: string, messages: ChatMessage[], mode?: 'restored' | 'fresh') => void;
  setActiveConversationFile: (agentId: string, filename: string | null | undefined) => void;
  /** True if the founder can manually invoke this agent right now. */
  isInvokable: (agentId: string) => boolean;
  setEra: (era: EraIndex) => void;
  /** Strategic pivot/kill: rewind to an earlier era and wipe done-state of downstream quests. */
  rewindToEra: (era: EraIndex) => void;
  setMgtPhase: (phase: number) => void;
  setVentureType: (vt: VentureType | null) => void;
  setOnboardingComplete: (value: boolean) => void;
  setPlan: (p: 'free' | 'founder' | 'beta') => void;
  showUpgradePrompt: (p: { era: number; reason: string }) => void;
  dismissUpgradePrompt: () => void;
  setWsStatus: (s: 'connecting' | 'connected' | 'reconnecting') => void;
  setSessionHydrated: (v: boolean) => void;
  setBunkers: (bunkers: BunkerSummaryWire[]) => void;
  setFounderStats: (stats: FounderStatsWire) => void;
  setActiveBunkerId: (id: string | null) => void;
  setJustCreatedBunkerId: (id: string | null) => void;
  /** Wipe the board to a clean slate, then apply the server's deliverable-presence
   *  snapshot. Clears manual overrides — used when switching to another bunker. */
  resetQuestsToServer: (completions: Record<string, boolean>) => void;
  setQuestsBunkerId: (id: string | null) => void;
  /** Set the UI theme (persists + applies the data-theme attribute). */
  setTheme: (t: ThemeId) => void;
  /** Advance to the next theme in cycle order (Topbar button). */
  cycleTheme: () => void;
  resetStore: () => void;
  startThinking: (agentId: string, inputTokens: number) => void;
  stopThinking: (agentId: string) => void;
  toggleQuest: (questId: string) => void;
  setQuestAutoCompleted: (questId: string, autoCompleted: boolean) => void;
  getEraQuests: (era: number) => Quest[];
  getEraProgress: (era: number) => { done: number; total: number };
  /** Total XP = Σ xp of done quests + Σ ERA_CLEAR_XP[e] for every cleared era (e < currentEraIndex). Pure-derived. */
  getTotalXp: () => number;
  /** Derived XP/level breakdown for the UI: { totalXp, level, rank, xpIntoLevel, xpForNext, pct }. */
  getXpState: () => {
    totalXp: number;
    level: number;
    rank: string;
    xpIntoLevel: number;
    xpForNext: number;
    pct: number;
  };
  setPendingUnlockAnimation: (era: number | null) => void;
  bumpInbox: (agentId: string) => void;
  bumpHistory: (agentId: string) => void;
  bumpArtifacts: (agentId: string) => void;
}

export const ERA_LABELS: Record<EraIndex, string> = {
  0: 'Era 0 — Identity',
  1: 'Era 1 — Atlas',
  2: 'Era 2 — Truth',
  3: 'Era 3 — Forge',
  4: 'Era 4 — Trade',
  5: 'Era 5 — Empire',
};

let eventCounter = 0;

export const useGameStore = create<GameStore>()(
  persist(
    (set, get) => ({
  selectedDwellerId: null,
  currentEraLabel: ERA_LABELS[0],
  currentEraIndex: 0,
  currentMgtPhase: 0,
  ventureType: null,
  onboardingComplete: false,
  plan: 'free',
  upgradePrompt: null,
  wsStatus: 'connecting',
  sessionHydrated: false,
  theme: loadTheme(),
  bunkers: [],
  bunkersLoaded: false,
  founderStats: null,
  activeBunkerId: null,
  questsBunkerId: null,
  justCreatedBunkerId: null,
  events: [],
  dwellerMovements: {},
  chats: {},
  thinkingAgents: {},
  quests: Object.fromEntries(QUESTS.map((q) => [q.id, { ...q }])),
  pendingUnlockAnimation: null,
  inboxBump: {},
  historyBump: {},
  artifactBump: {},
  conversationEpoch: {},
  conversationMode: {},
  activeConversationFile: {},

  selectDweller: (id) => set({ selectedDwellerId: id }),

  pushEvent: (text) =>
    set((state) => {
      eventCounter += 1;
      const entry: EventLogEntry = { id: eventCounter, timestamp: Date.now(), text };
      const next = [...state.events, entry];
      return { events: next.length > MAX_EVENTS ? next.slice(-MAX_EVENTS) : next };
    }),

  resetEvents: () => set({ events: [] }),

  dispatchDweller: (id, targetRoomId) =>
    set((state) => ({
      dwellerMovements: {
        ...state.dwellerMovements,
        [id]: { state: 'walking', targetRoomId, path: null },
      },
    })),

  _setMovement: (id, patch) =>
    set((state) => ({
      dwellerMovements: {
        ...state.dwellerMovements,
        [id]: { ...state.dwellerMovements[id], ...patch },
      },
    })),

  appendChatMessage: (agentId, role, content) =>
    set((state) => {
      const current = state.chats[agentId] || [];
      return {
        chats: {
          ...state.chats,
          [agentId]: [...current, { role, content }],
        },
      };
    }),

  clearChat: (agentId) =>
    set((state) => {
      const next = { ...state.chats };
      delete next[agentId];
      return {
        chats: next,
        // Force the next message into a brand-new conversation file.
        activeConversationFile: { ...state.activeConversationFile, [agentId]: null },
      };
    }),

  isInvokable: (agentId) =>
    isInvokable(agentId, get().currentEraIndex, get().ventureType),

  setEra: (era) =>
    set({
      currentEraIndex: era,
      currentEraLabel: ERA_LABELS[era] ?? `Era ${era}`,
    }),

  rewindToEra: (era) =>
    set((state) => {
      const firstMgtPhase = ERAS[era]?.mgtPhases[0] ?? 0;
      const quests = { ...state.quests };
      for (const id of Object.keys(quests)) {
        const q = quests[id];
        if (q.era > era) {
          quests[id] = { ...q, done: false, autoCompleted: false, manuallyOverridden: false };
        }
      }
      return {
        currentEraIndex: era,
        currentEraLabel: ERA_LABELS[era] ?? `Era ${era}`,
        currentMgtPhase: firstMgtPhase,
        quests,
      };
    }),

  setMgtPhase: (phase) => set({ currentMgtPhase: phase }),

  setVentureType: (vt) => set({ ventureType: vt }),

  setOnboardingComplete: (value) => set({ onboardingComplete: value }),

  setPlan: (p) => set({ plan: p }),

  showUpgradePrompt: (p) => set({ upgradePrompt: p }),

  dismissUpgradePrompt: () => set({ upgradePrompt: null }),

  setWsStatus: (s) => set({ wsStatus: s }),

  setSessionHydrated: (v) => set({ sessionHydrated: v }),

  setBunkers: (bunkers) => set({ bunkers, bunkersLoaded: true }),

  setFounderStats: (stats) => set({ founderStats: stats }),

  setActiveBunkerId: (id) => { setApiBunkerId(id); set({ activeBunkerId: id }); },

  setJustCreatedBunkerId: (id) => set({ justCreatedBunkerId: id }),

  resetQuestsToServer: (completions) =>
    set((state) => {
      const quests = { ...state.quests };
      for (const id of Object.keys(quests)) {
        const auto = completions[id] ?? false;
        quests[id] = { ...quests[id], done: auto, autoCompleted: auto, manuallyOverridden: false };
      }
      return { quests };
    }),

  setQuestsBunkerId: (id) => set({ questsBunkerId: id }),

  setTheme: (t) => {
    saveTheme(t);
    applyTheme(t);
    set({ theme: t });
  },

  cycleTheme: () => {
    const t = nextTheme(get().theme);
    saveTheme(t);
    applyTheme(t);
    set({ theme: t });
  },

  resetStore: () =>
    set({
      currentEraIndex: 0,
      currentEraLabel: ERA_LABELS[0],
      currentMgtPhase: 0,
      ventureType: null,
      onboardingComplete: false,
      quests: Object.fromEntries(QUESTS.map((q) => [q.id, { ...q }])),
    }),

  startThinking: (agentId, inputTokens) =>
    set((state) => ({
      thinkingAgents: {
        ...state.thinkingAgents,
        [agentId]: { startedAt: Date.now(), inputTokens },
      },
    })),

  stopThinking: (agentId) =>
    set((state) => {
      if (!state.thinkingAgents[agentId]) return state;
      const next = { ...state.thinkingAgents };
      delete next[agentId];
      return { thinkingAgents: next };
    }),

  loadConversation: (agentId, messages, mode = 'restored') =>
    set((state) => ({
      chats: { ...state.chats, [agentId]: messages },
      conversationEpoch: {
        ...state.conversationEpoch,
        [agentId]: (state.conversationEpoch[agentId] ?? 0) + 1,
      },
      conversationMode: { ...state.conversationMode, [agentId]: mode },
    })),

  setActiveConversationFile: (agentId, filename) =>
    set((state) => ({
      activeConversationFile: { ...state.activeConversationFile, [agentId]: filename },
    })),

  toggleQuest: (questId) =>
    set((state) => {
      const q = state.quests[questId];
      if (!q) return state;
      return {
        quests: {
          ...state.quests,
          [questId]: { ...q, done: !q.done, manuallyOverridden: true },
        },
      };
    }),

  setQuestAutoCompleted: (questId, autoCompleted) =>
    set((state) => {
      const q = state.quests[questId];
      if (!q) return state;
      // If founder manually overrode, don't auto-toggle
      if (q.manuallyOverridden) return state;
      return {
        quests: {
          ...state.quests,
          [questId]: { ...q, done: autoCompleted, autoCompleted },
        },
      };
    }),

  getEraQuests: (era) => {
    const all = get().quests;
    return Object.values(all).filter((q) => q.era === era);
  },

  getEraProgress: (era) => {
    const all = get().quests;
    const ofEra = Object.values(all).filter((q) => q.era === era && q.required);
    return { done: ofEra.filter((q) => q.done).length, total: ofEra.length };
  },

  getTotalXp: () => {
    const { quests, currentEraIndex } = get();
    let xp = 0;
    for (const q of Object.values(quests)) {
      if (q.done) xp += q.xp ?? 0;
    }
    for (let e = 0; e < currentEraIndex; e++) {
      xp += ERA_CLEAR_XP[e as EraIndex] ?? 0;
    }
    return xp;
  },

  getXpState: () => {
    const totalXp = get().getTotalXp();
    const b = xpBreakdown(totalXp);
    return { totalXp, level: b.level, rank: rankForLevel(b.level), xpIntoLevel: b.xpIntoLevel, xpForNext: b.xpForNext, pct: b.pct };
  },

  setPendingUnlockAnimation: (era) => set({ pendingUnlockAnimation: era }),

  bumpInbox: (agentId) =>
    set((state) => ({
      inboxBump: { ...state.inboxBump, [agentId]: (state.inboxBump[agentId] ?? 0) + 1 },
    })),

  bumpHistory: (agentId) =>
    set((state) => ({
      historyBump: { ...state.historyBump, [agentId]: (state.historyBump[agentId] ?? 0) + 1 },
    })),

  bumpArtifacts: (agentId) =>
    set((state) => ({
      artifactBump: { ...state.artifactBump, [agentId]: (state.artifactBump[agentId] ?? 0) + 1 },
    })),
    }),
    {
      name: 'foundr-x-game-store',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        onboardingComplete: state.onboardingComplete,
        ventureType: state.ventureType,
        currentEraIndex: state.currentEraIndex,
        currentMgtPhase: state.currentMgtPhase,
        quests: state.quests,
        questsBunkerId: state.questsBunkerId,
      }),
      // Merge strategy: take code-defined quests as base (so new quests are never lost),
      // then restore only done/autoCompleted from localStorage on top.
      merge: (persisted, current) => {
        const p = persisted as Partial<GameStore>;
        const persistedQuests = p.quests ?? {};
        const mergedQuests = { ...current.quests };
        for (const id of Object.keys(mergedQuests)) {
          const saved = persistedQuests[id];
          if (saved) {
            mergedQuests[id] = {
              ...mergedQuests[id],
              done: saved.done,
              autoCompleted: saved.autoCompleted ?? false,
            };
          }
        }
        // Derive the era label from the persisted index so the header stays in sync.
        const eraIdx = (p.currentEraIndex ?? current.currentEraIndex) as EraIndex;
        const eraLabel = ERA_LABELS[eraIdx] ?? `Era ${eraIdx}`;
        return { ...current, ...p, quests: mergedQuests, currentEraLabel: eraLabel };
      },
    },
  ),
);
